/**
 * Favorites + recently used blocks for the palette and Cmd+K (spec §4.3,
 * v1 = localStorage; server-side user settings stay an open question).
 * Pure factory for tests; provide/inject pair for the component tree.
 */

import { inject, provide, ref, watch } from 'vue';
import type { InjectionKey, Ref } from 'vue';

export interface PaletteEntry {
    kind: 'type' | 'shared';
    /** Type slug or shared block id (stringified). */
    key: string;
}

export interface PaletteHistory {
    favorites: Ref<PaletteEntry[]>;
    recent: Ref<PaletteEntry[]>;
    isFavorite(entry: PaletteEntry): boolean;
    toggleFavorite(entry: PaletteEntry): void;
    pushRecent(entry: PaletteEntry): void;
}

export interface PaletteStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
}

const HISTORY_KEY: InjectionKey<PaletteHistory> = Symbol('builder-v2-palette-history');

const RECENT_CAP = 8;

const sameEntry = (a: PaletteEntry, b: PaletteEntry): boolean => a.kind === b.kind && a.key === b.key;

function readEntries(storage: PaletteStorage, key: string): PaletteEntry[] {
    try {
        const raw = storage.getItem(key);
        const parsed: unknown = raw === null ? [] : JSON.parse(raw);

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.filter(
            (item): item is PaletteEntry =>
                typeof item === 'object' &&
                item !== null &&
                ((item as PaletteEntry).kind === 'type' || (item as PaletteEntry).kind === 'shared') &&
                typeof (item as PaletteEntry).key === 'string',
        );
    } catch {
        return [];
    }
}

function persistEntries(storage: PaletteStorage, key: string, entries: PaletteEntry[]): void {
    try {
        storage.setItem(key, JSON.stringify(entries));
    } catch {
        // private mode / quota — the palette works without history
    }
}

export function makePaletteHistory(storage?: PaletteStorage | null, storagePrefix = 'pb2'): PaletteHistory {
    const store = storage ?? (typeof localStorage === 'undefined' ? null : localStorage);
    const favoritesKey = `${storagePrefix}:favorites`;
    const recentKey = `${storagePrefix}:recent`;
    const favorites = ref<PaletteEntry[]>(store ? readEntries(store, favoritesKey) : []);
    const recent = ref<PaletteEntry[]>(store ? readEntries(store, recentKey) : []);

    if (store) {
        watch(favorites, (entries) => persistEntries(store, favoritesKey, entries));
        watch(recent, (entries) => persistEntries(store, recentKey, entries));
    }

    return {
        favorites,
        recent,
        isFavorite(entry) {
            return favorites.value.some((existing) => sameEntry(existing, entry));
        },
        toggleFavorite(entry) {
            favorites.value = this.isFavorite(entry)
                ? favorites.value.filter((existing) => !sameEntry(existing, entry))
                : [...favorites.value, entry];
        },
        pushRecent(entry) {
            recent.value = [entry, ...recent.value.filter((existing) => !sameEntry(existing, entry))].slice(
                0,
                RECENT_CAP,
            );
        },
    };
}

export function createPaletteHistory(storagePrefix = 'pb2'): PaletteHistory {
    const history = makePaletteHistory(null, storagePrefix);

    provide(HISTORY_KEY, history);

    return history;
}

export function usePaletteHistory(): PaletteHistory {
    const history = inject(HISTORY_KEY);

    if (!history) {
        throw new Error('usePaletteHistory must be used inside a BuilderV2 tree');
    }

    return history;
}
